import { StopId } from "@vygruppen/vy-train-map";
import { InfrastructureResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/responseSchema";
import { CommonTrainInfoFormProps } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/TrainInfoForm";
import { CommonSubTypeProps } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/TrainInfoFormModal";
import {
  AffectedStopOption,
  getToStopOptions,
  routeSectionMethods,
  splitPassedStopsAndBuildOptGroups,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/utils/affectedStopsUtils";
import { TrainContext } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/utils/formContextWrappers";
import { toNonExcludedStops } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/utils/listofExcludedStations";
import {
  EitherTrainFormSchema,
  RouteSection,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { useTrainInfoContext } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/useTrainInfoContext";
import { TrainEventTypeEnum } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainEventTypeEnum";
import { TrainRouteStop } from "features/CenterContent/VehicleDetails/TrainDetails/useTrainRoute";
import { log, LogLevel } from "logging/datadogBrowserLogs";
import { FC, useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  DropsStaticAlert,
  HStack,
  Radio,
  RadioGroup,
  Skeleton,
  Text,
  Tooltip,
  VStack,
} from "shared/components/Spor";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { ExpandableInfoMessage } from "shared/components/feedback/InfoMessage/ExpandableInfoMessage/ExpandableInfoMessage";
import { RenderErrorInPath } from "shared/components/forms/RenderErrorInPath";
import { Select } from "shared/components/forms/Select";

import { useCountryCodeForDropsRole } from "shared/hooks/useCountryCodeForDropsRole";
import { CountryCode, getStopContext } from "shared/types/countryCode";
import { getFieldFromNestedObject } from "shared/utils/objectUtils";

// Helper function to convert TrainRouteStop to stopIdSchema format
export const trainRouteStopToStopId = (
  stop: TrainRouteStop,
  countryCode: CountryCode,
): StopId => ({
  id: stop.stopId,
  name: stop.name,
  context: getStopContext(countryCode),
});

type AffectedStopsFieldsProps = {
  alreadySelectedStops?: string[];
  infrastructureEvent?: InfrastructureResponse;
};

export const AffectedStopsFields: FC<
  CommonTrainInfoFormProps &
    CommonSubTypeProps &
    AffectedStopsFieldsProps &
    TrainContext
> = ({
  mode,
  trainId,
  alreadySelectedStops,
  infrastructureEvent,
  isProductionDeviation,
  trainFormPrefix,
}) => {
  const { t } = useTranslation();
  const countryCode = useCountryCodeForDropsRole();
  const {
    getValues,
    setValue,
    control,
    resetField,
    register,
    unregister,
    formState,
  } = useFormContext<EitherTrainFormSchema>();
  const { errors } = formState;

  const fromStopFieldPath =
    `${trainFormPrefix}.trainRouteSection.fromStop` as const;

  const toStopFieldPath =
    `${trainFormPrefix}.trainRouteSection.toStop` as const;

  const typeFieldPath = `${trainFormPrefix}.trainRouteSection.type` as const;

  const affectedStopsFieldPath = `${trainFormPrefix}.affectedStops` as const;

  const trainTypeFieldPath = `${trainFormPrefix}.type` as const;

  const trainEventType = getValues(trainTypeFieldPath); // Used for autofilling only

  const { useTrainRouteBatch } = useTrainInfoContext();
  const { data: trainRoute, status: fetchStatus } = useTrainRouteBatch(trainId);

  const stops = useMemo(() => trainRoute?.stops ?? [], [trainRoute]);
  const selectableStops = useMemo(() => toNonExcludedStops(stops), [stops]); // Stops you can select from <Select/>

  const {
    findStop,
    toRouteSection,
    autofillInfrastructure,
    autofillTrainRoute,
  } = useMemo(
    () => routeSectionMethods(stops, selectableStops, countryCode),
    [stops, selectableStops, countryCode],
  );

  const setRouteSection = (routeSection: RouteSection) => {
    const { type, fromStop } = routeSection;
    const toStop = type !== "AT_STOP" ? routeSection.toStop : fromStop;
    setValue(typeFieldPath, type);
    setValue(fromStopFieldPath, fromStop);
    if (type !== "AT_STOP") {
      setValue(toStopFieldPath, toStop);
    } else {
      unregister(toStopFieldPath);
    }

    const fromIndex = stops.findIndex((stop) => stop.stopId === fromStop.id);
    const toIndex = stops.findIndex((stop) => stop.stopId === toStop.id);
    const affectedStops = stops
      .slice(fromIndex, toIndex + 1)
      .map((stop) => trainRouteStopToStopId(stop, countryCode));
    setValue(affectedStopsFieldPath, affectedStops);
  };

  /**
   * Since we can choose "Velg stasjon" and thus make illegal states,
   * we use this function to update the stops regardless of state.
   * Otherwise, setRouteSection will update both stops and affectedStops
   */
  const updateStopFields = (
    fromStop: TrainRouteStop | undefined,
    toStop: TrainRouteStop | undefined,
  ) => {
    if (fromStop) {
      setValue(
        fromStopFieldPath,
        trainRouteStopToStopId(fromStop, countryCode),
      );
    } else {
      unregister(fromStopFieldPath);
    }
    if (toStop) {
      setValue(toStopFieldPath, trainRouteStopToStopId(toStop, countryCode));
    } else {
      unregister(toStopFieldPath);
    }
  };

  const fromStopId = getValues(fromStopFieldPath)?.id;
  const toStopId = getValues(toStopFieldPath)?.id;

  const stopSelectionType = getValues(typeFieldPath);

  const firstStop = selectableStops.at(0);
  const lastStop = selectableStops.at(selectableStops.length - 1);

  const fromStopIsLastStop =
    stops.length > 0 && !!fromStopId && fromStopId === lastStop?.stopId;

  const incidentIdField = `${trainFormPrefix}.incidentId`;
  const incidentId = getValues(incidentIdField);

  // Attempt to autofill the form
  useEffect(() => {
    // Do nothing if route is empty or the fetch is pending
    if (stops.length === 0 || fetchStatus !== "success") {
      return;
    }

    // Necessary to update `affectedStops` and `trainRouteSection.type`
    // (not stored in backend) and it also removes autofill for edit
    if (mode === "edit" && fromStopId) {
      const fromStop = findStop(fromStopId)!;
      const toStop = findStop(toStopId) ?? fromStop;
      const routeSection = toRouteSection(fromStop, toStop);
      setRouteSection(routeSection);
      return;
    }

    // This check is only necessary because of trainBatchForm/productionDeviation.
    // As they causes a rerender of `AffectedStopsFields` by switching trainSerie.
    // Otherwise, the only way to rerender `AffectedStopsFields` is by switching
    // trainEventType, thus we could've just had a new autofill at every render.
    const changedTrainEventType = getFieldFromNestedObject(
      formState.dirtyFields,
      trainTypeFieldPath,
    );
    const hasAutofilled = stopSelectionType !== undefined;
    // Make a new autofill at render if you just switched trainEventType
    if (hasAutofilled && !changedTrainEventType) {
      return;
    }

    // Unregister trainEventType so that the above check still works
    unregister(trainTypeFieldPath, { keepValue: true });

    // TRAIN_STOPPED should at most have 1 or 2 affectedStops, therefore we can't
    // let it be autofilled by InfrastructureEvent or productionDeviation.
    if (trainEventType === TrainEventTypeEnum.TRAIN_STOPPED) {
      const routeSection = autofillTrainRoute(trainRoute, "fromStop", "toStop");
      setRouteSection(routeSection);
      return;
    }

    // Handle specific train event special cases
    if (
      [
        TrainEventTypeEnum.TRAIN_CAPACITY_REDUCED,
        TrainEventTypeEnum.TRAIN_CAPACITY_INCREASED,
        TrainEventTypeEnum.TRAIN_MISSING_PRODUCT,
      ].includes(trainEventType)
    ) {
      const routeSection = toRouteSection(firstStop!, lastStop!);
      setRouteSection(routeSection);
      return;
    }

    // Try to autofill from InfrastructureEvent
    if (infrastructureEvent) {
      const routeSection = autofillInfrastructure(infrastructureEvent);
      if (!routeSection) return;
      setRouteSection(routeSection);
      return;
    }

    // Fill the entire route by default for productionDeviation
    if (isProductionDeviation) {
      const routeSection = toRouteSection(firstStop!, lastStop!);
      setRouteSection(routeSection);
      return;
    }

    if (
      [
        TrainEventTypeEnum.TRAIN_DELAY_EXPECTED,
        TrainEventTypeEnum.TRAIN_DELAYED,
      ].includes(trainEventType)
    ) {
      const routeSection = autofillTrainRoute(trainRoute, "toStop");
      setRouteSection(routeSection);
      return;
    }

    if ([TrainEventTypeEnum.TRAIN_LATE_TO_TRACK].includes(trainEventType)) {
      const routeSection = toRouteSection(firstStop!, firstStop!);
      setRouteSection(routeSection);
      return;
    }

    // Default
    const routeSection = autofillTrainRoute(trainRoute, "fromStop", "lastStop");
    setRouteSection(routeSection);
  }, [
    fetchStatus,
    infrastructureEvent,
    stops,
    incidentId,
    trainFormPrefix,
    trainEventType,
  ]);

  // Validate consistency of fromStop and toStop along route
  useEffect(() => {
    if (!stopSelectionType || stopSelectionType === "AT_STOP") {
      // Do nothing if state is invalid or if AT_STOP (which is always internally consistent)
      return;
    }

    if (fromStopIsLastStop) {
      // If the last stop is selected as fromStop, and the stopSelectionType is not AT_STOP,
      // toStop would be an empty list and the form would not be submitable.
      // Set the stopSelectionType to AT_STOP to ensure valid state.
      setValue(typeFieldPath, "AT_STOP");
    }

    // Should not be able to select a toStop that is equal to or before the fromStop
    const fromStopIndex = stops.findIndex((s) => s.stopId === fromStopId);
    const toStopIndex = stops.findIndex((s) => s.stopId === toStopId);
    if (fromStopIndex === -1 || toStopIndex === -1) {
      // Neither fromStop nor toStop are in the route. This may be due to switching between Tabs in
      // the batch modal – do nothing and assume it will pass, but log it just in case
      log(
        LogLevel.warn,
        "AffectedStopsFields",
        "either fromStop, toStop or both are not in the selected train's route.",
      );
    } else if (
      stopSelectionType === "BETWEEN_STOPS" &&
      toStopIndex <= fromStopIndex
    ) {
      // toStop is on or before fromStop in the route, empty it
      resetField(toStopFieldPath);
    }
  }, [fromStopId, typeFieldPath]);

  // Register the affectedStops field with the form
  useEffect(() => {
    register(affectedStopsFieldPath);
  }, [register, affectedStopsFieldPath]);

  // Group stops by whether the train has passed them or not
  const displayStops = useMemo(
    () => splitPassedStopsAndBuildOptGroups(selectableStops),
    [stops],
  );

  // Compute whether we should show an info box warning that the train has already
  // passed the fromStop.
  const showTrainPassedWarningBox = useMemo(() => {
    if (infrastructureEvent || stopSelectionType === "WHOLE_ROUTE")
      return false;

    const stopToCompare =
      stopSelectionType === "AT_STOP" ? fromStopId : toStopId;
    const selectedStop = stops.find((s) => s.stopId === stopToCompare);
    return !!selectedStop?.isArrived;
  }, [stops, fromStopId, toStopId, infrastructureEvent]);

  // Compute whether any of the selected affectedStops are already affected for this train
  const stopsAlreadySelected = useMemo(() => {
    const stopIds = stops.map((s) => s.stopId);

    const selectedStopIds = stopIds.slice(
      fromStopId ? stopIds.indexOf(fromStopId) : 0,
      toStopId ? stopIds.indexOf(toStopId) + 1 : -1,
    );

    return (
      (alreadySelectedStops
        ?.filter((stop) => selectedStopIds.includes(stop))
        .map((stopId) => stops.find((s) => s.stopId === stopId))
        .filter((s) => s !== undefined) as TrainRouteStop[]) ?? []
    );
  }, [fromStopId, toStopId, alreadySelectedStops, stops]);

  // Compute the options for the toStop Select
  const memoGetToSelect = useMemo(
    () => getToStopOptions(selectableStops, fromStopId),
    [stops, fromStopId],
  );

  if (fetchStatus === "pending") {
    return <Skeleton height="36px" />;
  }

  if (fetchStatus === "error" || !trainRoute || trainRoute.stops.length <= 0) {
    return (
      <FailureMessage
        customMessage={`${t("Kunne ikke hente ut stoppene til toget.")} ${t("Prøv på nytt, eller kontakt IT hvis feilen vedvarer.")}`}
      />
    );
  }

  const betweenRouteDisabled =
    trainEventType === TrainEventTypeEnum.TRAIN_LATE_TO_TRACK ||
    fromStopIsLastStop;
  const wholeRouteDisabled =
    trainEventType === TrainEventTypeEnum.TRAIN_LATE_TO_TRACK ||
    trainEventType === TrainEventTypeEnum.TRAIN_STOPPED;

  return (
    <VStack width="100%" alignItems="flex-start" gap="18px" my="6px">
      <Controller
        control={control}
        name={typeFieldPath}
        render={({ field }) => (
          <RadioGroup
            name={t("Stasjonsvalg")}
            value={field.value ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = e.target;
              field.onChange(value); // This alone is enough for "BETWEEN_STOP"
              if (value === "WHOLE_ROUTE") {
                const routeSection = toRouteSection(firstStop!, lastStop!);
                setRouteSection(routeSection);
              } else if (value === "AT_STOP") {
                // Necessary in order to remove toStop
                const fromStop = findStop(fromStopId);
                updateStopFields(fromStop, fromStop);
                if (!fromStop) return;
                const routeSection = toRouteSection(fromStop, fromStop);
                setRouteSection(routeSection);
              }
            }}
            // Forces a re-mount of the checkbox, since it can't handle external edits. Specifically when
            // you change "fromStop" to lastStop, which changes "BETWEEN_STOPS" -> "AT_STOPS" externally
            key={stopSelectionType}
          >
            <Radio value="AT_STOP">{t("På stasjon")}</Radio>
            <Radio value="BETWEEN_STOPS" disabled={betweenRouteDisabled}>
              <Tooltip
                label={
                  fromStopIsLastStop
                    ? t("Velg annen på stasjon-stopp enn siste stopp")
                    : undefined
                }
              >
                {t("Mellom stasjoner")}
              </Tooltip>
            </Radio>
            <Radio value="WHOLE_ROUTE" disabled={wholeRouteDisabled}>
              {t("Hel strekning")}
            </Radio>
          </RadioGroup>
        )}
      />

      <VStack gap="12px" width="100%" alignItems="flex-start">
        <HStack width="100%">
          <VStack gap={0} flexBasis={1} flexGrow={1} alignItems="stretch">
            <Select
              name={fromStopFieldPath}
              value={fromStopId ?? ""}
              onChange={(e) => {
                const newToStopId =
                  stopSelectionType === "AT_STOP" ? e.target.value : toStopId;

                const fromStop = findStop(e.target.value);
                const toStop = findStop(newToStopId);
                updateStopFields(fromStop, toStop);
                if (!fromStop || !toStop) return;
                const routeSection = toRouteSection(fromStop, toStop);
                setRouteSection(routeSection);
              }}
              minWidth="200px"
              width="100%"
              label={
                stopSelectionType === "AT_STOP"
                  ? `${t("På stasjon")}*`
                  : t("Fra og med stasjon*")
              }
              placeholder={t("Velg stasjon")}
              disabled={stopSelectionType === "WHOLE_ROUTE"}
              data-testid="select-stopField-from"
            >
              {infrastructureEvent
                ? selectableStops.map((stop) => (
                    <AffectedStopOption key={stop.stopId} stop={stop} />
                  ))
                : displayStops}
            </Select>
            <RenderErrorInPath errors={errors} errorPath={fromStopFieldPath} />
          </VStack>

          {stopSelectionType !== "AT_STOP" && (
            <VStack gap={0} flexBasis={1} flexGrow={1} alignItems="stretch">
              <Select
                name={toStopFieldPath}
                value={toStopId ?? ""}
                onChange={(e) => {
                  const fromStop = findStop(fromStopId);
                  const toStop = findStop(e.target.value);
                  updateStopFields(fromStop, toStop);
                  if (!fromStop || !toStop) return;
                  const routeSection = toRouteSection(fromStop, toStop);
                  setRouteSection(routeSection);
                }}
                minWidth="200px"
                width="100%"
                label={t("Til og med stasjon*")}
                placeholder={t("Velg stasjon")}
                disabled={stopSelectionType === "WHOLE_ROUTE"}
                data-testid="select-stopField-to"
              >
                {memoGetToSelect}
              </Select>
              <RenderErrorInPath errors={errors} errorPath={toStopFieldPath} />
            </VStack>
          )}
        </HStack>
      </VStack>

      {showTrainPassedWarningBox && !isProductionDeviation && (
        <DropsStaticAlert w="100%" flexBasis="100%" variant="info">
          {t("Toget har allerede passert stasjonen.")}
        </DropsStaticAlert>
      )}

      {stopsAlreadySelected.length > 0 && (
        <ExpandableInfoMessage
          semantics="warning"
          title={t("Stopp på valgt strekning er allerede innstilt")}
        >
          <VStack alignItems="flex-start">
            <Text variant="xs" pt="12px" fontWeight="bold">
              {t("Følgende stopp er allerede innstilt:")}
            </Text>
            <Text variant="xs">
              {stopsAlreadySelected.map((s) => s.name.trim()).join(", ")}
            </Text>
          </VStack>
        </ExpandableInfoMessage>
      )}
    </VStack>
  );
};
